/**
 * useCompanyCash — bank-true cash + lowest projected balance for the company
 * home verdict (Home Redesign). Thin wrapper over api.getCashFlowForecast.
 *
 * Honesty: cash is only "known" (bank-true) when a statement is reconciled.
 * The supervisor seeds the forecast's starting balance from the last reconciled
 * statement, or 0 if none. We treat a $0 start as UNKNOWN so the verdict never
 * raises a false "can't make payroll" alarm off an unreconciled $0.
 *
 * `enabled` gates the fetch by role (cashflow is finance-read tier) so viewers
 * don't fire a 403.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isInLocalMode } from '@/lib/api';
import { useAppStore } from '@/stores/useAppStore';

export interface CompanyCash {
  cashCents: number | null;
  cashKnown: boolean;
  lowestProjectedCents: number | null;
  caveats: string[];
}

const EMPTY: CompanyCash = { cashCents: null, cashKnown: false, lowestProjectedCents: null, caveats: [] };

export function useCompanyCash(enabled = true) {
  const { activeEntity } = useAppStore();
  const [cash, setCash] = useState<CompanyCash>(EMPTY);
  const [loading, setLoading] = useState(true);
  const cancelRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled || isInLocalMode() || !activeEntity?.id) {
      setCash(EMPTY);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.getCashFlowForecast();
      const forecast = r.forecast || [];
      const start = forecast.length > 0 ? forecast[0].runningBalance : null;
      // Bank-true only when a statement anchors a non-zero starting balance.
      const known = start != null && start !== 0;
      const lowest =
        forecast.length > 0 ? Math.min(...forecast.map((w) => w.runningBalance)) : null;
      if (!cancelRef.current) {
        setCash({
          cashCents: start != null ? Math.round(start * 100) : null,
          cashKnown: known,
          lowestProjectedCents: lowest != null ? Math.round(lowest * 100) : null,
          caveats: r.caveats || [],
        });
      }
    } catch {
      // Non-fatal (often a role 403 for viewers) — cash stays unknown; the
      // verdict falls back to non-cash signals and prompts to reconcile.
      if (!cancelRef.current) setCash(EMPTY);
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [activeEntity?.id, enabled]);

  useEffect(() => {
    cancelRef.current = false;
    refresh();
    return () => {
      cancelRef.current = true;
    };
  }, [refresh]);

  return { cash, loading, refresh };
}
